import React, { FC, ReactText } from 'react';
import { Button, Cascader, Form, Space } from 'antd';
import { CascaderOptionType } from 'antd/lib/cascader';
import {
  AddTaskResourceFormEnum,
  ITaskAvailableResource,
  ITaskAvailableResourceChilden,
  ITaskAvailableResourceOption,
} from '../../slice/types';
import { useTranslation } from 'react-i18next';
import { checkIsPositiveNumber } from 'utils/validationHelpers';
import NumberInput from 'app/components/NumberInput/NumberInput';

const ADD_RESOURCE_FORM_ID = 'addResourceToTaskForm';

interface IProps {
  onFormSubmit: (resource: ITaskAvailableResource, norm: number) => void;
  availableResources: ITaskAvailableResourceOption[];
}

const AddTaskResourceForm: FC<IProps> = ({
  onFormSubmit,
  availableResources,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const filter = (inputValue: string, path: CascaderOptionType[]) => {
    return path.some(
      option =>
        String(option.label).toLowerCase().indexOf(inputValue.toLowerCase()) >
        -1,
    );
  };

  const onChange = (
    value: ReactText[],
    selectedOptions?: CascaderOptionType[],
  ) => {
    const selected = selectedOptions as ITaskAvailableResourceChilden[];

    const selectedResource = selected[1]?.resource;

    if (selectedResource) {
      form.setFieldsValue({
        norm: selectedResource.recommendedNorm,
      });
    }
  };

  const onFinish = (values: {
    [AddTaskResourceFormEnum.resourceTypeIdAndResourceId]: number[];
    [AddTaskResourceFormEnum.norm]: string | number;
  }) => {
    const resourceId =
      values[AddTaskResourceFormEnum.resourceTypeIdAndResourceId][
        values[AddTaskResourceFormEnum.resourceTypeIdAndResourceId].length - 1
      ];

    const resources: ITaskAvailableResource[] = availableResources.reduce(
      (accumulator: ITaskAvailableResource[], item) => {
        let resources: ITaskAvailableResource[] = item.children.reduce(
          (accumulator: ITaskAvailableResource[], child) => {
            if (child.children) {
              const childResources = child.children.map(
                variation => variation.resource,
              );
              accumulator.push(...childResources);
            } else {
              accumulator.push(child.resource);
            }
            return accumulator;
          },
          [],
        );

        return [...accumulator, ...resources];
      },
      [],
    );
    const resource = resources.find(resource => resource.id === resourceId);
    if (resource) {
      onFormSubmit(resource, Number(values[AddTaskResourceFormEnum.norm]));
    }

    form.resetFields();
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ layout: 'vertical' }}
      requiredMark="optional"
      id={ADD_RESOURCE_FORM_ID}
      onFinish={onFinish}
    >
      <Space style={{ marginTop: 12 }} align="start">
        <Form.Item
          label={t('Task.CreateOrUpdateTaskResourcesCard.Resource', 'Resource')}
          name={AddTaskResourceFormEnum.resourceTypeIdAndResourceId}
          rules={[{ required: true }]}
        >
          <Cascader
            options={availableResources}
            onChange={onChange}
            placeholder={t(
              'Task.CreateOrUpdateTaskResourcesCard.Resource',
              'Resource',
            )}
            showSearch={{ filter, matchInputWidth: false }}
          />
        </Form.Item>

        <Form.Item
          label={t('Task.CreateOrUpdateTaskResourcesCard.Norm', 'Norm')}
          name={AddTaskResourceFormEnum.norm}
          rules={[{ required: true }, { validator: checkIsPositiveNumber }]}
        >
          <NumberInput
            placeholder={t('Task.CreateOrUpdateTaskResourcesCard.Norm', 'Norm')}
          />
        </Form.Item>

        <Button
          type="primary"
          form={ADD_RESOURCE_FORM_ID}
          key="submit"
          htmlType="submit"
          style={{ marginTop: 30 }}
        >
          {t(
            'Task.CreateOrUpdateTaskResourcesCard.AddResource',
            'Add Resource',
          )}
        </Button>
      </Space>
    </Form>
  );
};

export default AddTaskResourceForm;
